import { AfterViewInit, Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ConnectedCustomProperty } from '../../model/connected-custom-property';
import { ObjectComponent } from '../../model/object-component';
import { ObjectComponentCategory } from '../../model/object-component-category';
import { ObjectComponentPropertyValue } from '../../model/object-component-property-value';
import { ObjectComponentType } from '../../model/object-component-type';
import { PlannedMaintenanceAction } from '../../model/object-component-planned-maintenance';
import { DocumentTypeMimeTypes } from '../../../core/model/const/document-type-mime-types';
import { ComponentService } from '../../../rent/service/data/component.service.';
import { AstralButtonSize } from '../../../core/component/astral-components/astral-button/astral-button.component';

@Component({
    selector: 'app-object-component',
    templateUrl: './object-component.component.html',
    styleUrls: ['./object-component.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ObjectComponentComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(private _cf: FormControlExFactory, private _fb: FormBuilder, public readonly componentService: ComponentService) {}

  @Input() componentCategories: ObjectComponentCategory[];
  private _forms: FormArray;
  @Input() set forms(value: AbstractControl) {
    this._forms = value as FormArray;
  }
  get forms(): FormArray {
    return this._forms;
  }
  @Input() rootForm: FormGroup;
  @Input() component: ObjectComponent;
  @Input() collection: ObjectComponent[];
  @Input() parentIsNew: boolean;
  @Input() rentalObjectId: string;
  @Input() actionButtonSize: AstralButtonSize = 'small';
  private _subscription = new Subscription();

  categorySelection: SelectionItem[];
  typeSelection: SelectionItem[];
  form: FormGroup;

  mimes: string[] = DocumentTypeMimeTypes.PDF().concat(DocumentTypeMimeTypes.WebImage());

  ngOnDestroy(): void {
    if (this._subscription) this._subscription.unsubscribe();
  }

  delete() {
    const index = this.collection.findIndex(i => i.id === this.component.id);
    this.forms.controls.splice(index, 1);
    this.collection.splice(index, 1);
    this.forms.markAsDirty();
    this.rootForm.markAsDirty();
  }

  customProperties: ConnectedCustomProperty[] = [];
  plannedMaintenanceAction: PlannedMaintenanceAction;
  plannedMaintenanceActionForm: FormGroup;

  ngOnInit(): void {
    this.form = this.createComponentForm(this.component);
    this.forms.controls.push(this.form);
    this.customProperties = this.getComponentType(this.component.componentTypeId).customProperties;
    this.categorySelection = this.componentCategories.map(c => new SelectionItem(c.name, c.id));
    this.typeSelection = this.getComponentCategoryForType(this.component.componentTypeId).objectComponentTypes.map(
      t => new SelectionItem(t.name, t.id)
    );
    this.component.attachments.forEach(item => {
      (this.form.controls['attachments'] as FormArray).push(
        this._fb.group({
          id: item.id,
          caption: item.caption,
          mimeType: item.mimeType,
          title: item.caption,
          src: null, // hack
          data: false,// hack
        })
      );
    });
  }

  ngAfterViewInit(): void {
    // Temporary fix. Not sure how to handle the dirty states correct..
    this._subscription.add(
      this.form.valueChanges.subscribe(v => {
        this.rootForm.markAsDirty();
      })
    );
    // TODO: uncomment after 'Planned Maintenance Actions' approvement
    // this._subscription.add(this.plannedMaintenanceActionForm.valueChanges.subscribe(x => this.rootForm.markAsDirty()));
  }

  private createComponentForm(component: ObjectComponent): FormGroup {
    const group = this._fb.group({
      id: this._cf.createHidden(component.id),
      componentCategory: this._cf.createMandatory(
        'COMPONENT.COMPONENT_CATEGORY',
        this.getComponentCategoryForType(component.componentTypeId).id
      ),
      componentTypeId: this._cf.createMandatory('COMPONENT.TYPE', component.componentTypeId),
      installationYear: this._cf.create('COMPONENT.INSTALLED_YEAR', component.installationYear),
      installationMonth: this._cf.create('COMPONENT.INSTALLED_MONTH', component.installationMonth),
      name: this._cf.create('COMPONENT.NAME', component.name, Validators.maxLength(40)),
      comment: this._cf.create('COMMON.NOTE', component.comment, Validators.maxLength(200)),
      customPropertyValues: this._fb.array([]),
      attachments: this._fb.array([]),
      plannedMaintenanceActions: this._fb.array([]),
    });
    this._subscription.add(
      group.controls['componentCategory'].valueChanges.subscribe(val => {
        const category = this.getComponentCategory(val);
        if (group.controls['componentTypeId'].value !== category.objectComponentTypes[0].id) {
          this.typeSelection = category.objectComponentTypes.map(t => new SelectionItem(t.name, t.id));
          group.controls['componentTypeId'].setValue(category.objectComponentTypes[0].id);
        }
      })
    );
    this._subscription.add(
      group.controls['componentTypeId'].valueChanges.subscribe(val => {
        let type = this.getComponentType(val);
        (this.form.controls['customPropertyValues'] as FormArray).clear();
        this.component.customPropertyValues = type.customProperties.map(
          v => new ObjectComponentPropertyValue({ customProperty: v.property })
        );
        this.customProperties = type.customProperties;
      })
    );

    // TODO: uncomment after 'Planned Maintenance Actions' approvement

    // At this component only one planned maintenance action is allowed even model allows have multiple items
    // this.plannedMaintenanceAction = this.component.plannedMaintenanceActions.length > 0
    //   ? this.component.plannedMaintenanceActions[0]
    //   : new PlannedMaintenanceAction();

    // this.plannedMaintenanceActionForm = this.createPlannedMaintenanceActionValueForm(this.plannedMaintenanceAction);
    // (group.controls['plannedMaintenanceActions'] as FormArray).controls.push(this.plannedMaintenanceActionForm);

    return group;
  }

  private createPlannedMaintenanceActionValueForm(plannedMaintenanceAction: PlannedMaintenanceAction): FormGroup {
    let group = this._fb.group({
      id: this._cf.createHidden(plannedMaintenanceAction.id),
      plannedMaintenanceCaption: this._cf.create(
        'COMPONENT.PLANNED_MAINTENANCE_ACTION.CAPTION',
        plannedMaintenanceAction.plannedMaintenanceCaption
      ),
      latestMaintenanceExecution: this._fb.group({}),
      plannedMaintenanceInterval: this._cf.create(
        'COMPONENT.PLANNED_MAINTENANCE_ACTION.INTERVAL',
        plannedMaintenanceAction.plannedMaintenanceInterval
      ),
    });

    return group;
  }

  private getComponentCategory(categoryId: string): ObjectComponentCategory {
    return this.componentCategories.find(c => c.id === categoryId);
  }

  private getComponentCategoryForType(componentTypeId: string): ObjectComponentCategory {
    return this.componentCategories.find(c => c.objectComponentTypes.find(t => t.id === componentTypeId) !== undefined);
  }

  private getComponentType(componentTypeId: string): ObjectComponentType {
    const category = this.getComponentCategoryForType(componentTypeId);
    return category.objectComponentTypes.find(t => t.id === componentTypeId);
  }
}
